// ============================================================
// Tableau de bord des absences — vues, tri et RÉGLETTE annuelle. Moteur PUR, aucune dépendance
// Angular. Testé sans TestBed : hr-leave-board.spec.ts.
// ============================================================
//
// Une liste d'absences écrite en toutes lettres se lit ligne à ligne : « 27/07/2026 → 01/08/2026 ».
// À dix lignes cela passe ; à quatre cents salariés, personne ne voit plus QUAND les absences
// tombent, ni qu'elles se chevauchent. Ce module donne les deux choses qui manquent :
//
//   LA POSITION — chaque période devient un segment sur une réglette de douze mois (`leaveRibbonOf`).
//                 Même échelle pour toutes les lignes, donc les segments s'alignent verticalement :
//                 un pic d'août se voit sans lire une seule date.
//
//   L'ORDRE     — ce qui attend une décision passe devant (`compareLeaveRows`). Une demande en
//                 attente est un TRAVAIL À FAIRE ; une absence approuvée est une archive. Les
//                 mettre sur le même plan, c'est obliger le responsable à chercher son propre
//                 travail au milieu de l'historique.
//
// Les VUES (`LeaveView`) croisent l'état de la décision et le temps : « en attente » est un état,
// « passée » n'en est pas un — c'est une date de fin dépassée. Les deux notions se ressemblent à
// l'écran mais ne se rangent pas au même endroit dans la donnée, d'où ce croisement fait ici, une
// fois, plutôt que dispersé dans le composant.

import { HR_ABSENCE_STATE_APPROVED, HR_ABSENCE_STATE_REQUESTED } from '@app/shared/constants';

// ============================================================
// Vues
// ============================================================

export type LeaveView = 'todo' | 'current' | 'upcoming' | 'past' | 'closed' | 'all';

export const LEAVE_VIEWS: { value: LeaveView; label: string; hint: string }[] = [
	{ value: 'todo', label: 'À traiter', hint: 'Demandes en attente de validation' },
	{ value: 'current', label: 'En cours', hint: 'Absences approuvées qui couvrent aujourd\'hui' },
	{ value: 'upcoming', label: 'À venir', hint: 'Absences approuvées qui n\'ont pas encore commencé' },
	{ value: 'past', label: 'Passées', hint: 'Absences approuvées et terminées' },
	{ value: 'closed', label: 'Refusées / annulées', hint: 'Demandes sans suite' },
	{ value: 'all', label: 'Toutes', hint: 'Tout ce qui est enregistré sur l\'année' },
];

// Où l'absence se situe DANS LE TEMPS, indépendamment de la décision.
//
// Une absence OUVERTE (constatée le matin, fin inconnue) n'a pas de fin à comparer : sa `dateend`
// n'est que le dernier jour CONNU. Elle court jusqu'à sa clôture, donc elle est « en cours » tant
// qu'elle a commencé — la ranger dans les passées la ferait disparaître de l'écran du responsable
// le lendemain, alors que c'est précisément elle qui attend une suite.
export type LeaveTime = 'current' | 'upcoming' | 'past';

export function leaveTimeOf(a: { datestart: string; dateend: string; open?: boolean }, today: string): LeaveTime {
	if (a.datestart > today) return 'upcoming';
	if (a.open) return 'current';
	return a.dateend < today ? 'past' : 'current';
}

// La ligne appartient-elle à la vue demandée ?
export function leaveViewMatches(view: LeaveView, state: number, time: LeaveTime): boolean {
	switch (view) {
		case 'all': return true;
		case 'todo': return state === HR_ABSENCE_STATE_REQUESTED;
		case 'closed': return state !== HR_ABSENCE_STATE_REQUESTED && state !== HR_ABSENCE_STATE_APPROVED;
		// Les trois vues temporelles ne parlent que des absences DÉCIDÉES : une demande en attente
		// qui commence demain relève de « à traiter », pas de « à venir » — sinon elle serait
		// comptée deux fois et le responsable croirait le travail fait.
		default: return state === HR_ABSENCE_STATE_APPROVED && time === view;
	}
}

// ============================================================
// Tri — ce qui attend une décision d'abord
// ============================================================
//
// Deux blocs, deux logiques :
//   • EN ATTENTE : par date de DÉBUT croissante. La plus urgente est celle qui commence le plus
//     tôt — répondre le 30 juillet à un congé qui commençait le 27 n'est plus une réponse.
//   • LE RESTE : par date de début décroissante, l'usage d'un historique (le récent d'abord).
export function compareLeaveRows(
	a: { state: number; datestart: string; id: number },
	b: { state: number; datestart: string; id: number },
): number {
	const pa = a.state === HR_ABSENCE_STATE_REQUESTED ? 0 : 1;
	const pb = b.state === HR_ABSENCE_STATE_REQUESTED ? 0 : 1;
	if (pa !== pb) return pa - pb;
	const byDate = pa === 0
		? a.datestart.localeCompare(b.datestart)
		: b.datestart.localeCompare(a.datestart);
	return byDate !== 0 ? byDate : b.id - a.id;
}

// ============================================================
// Réglette annuelle
// ============================================================

export interface LeaveRibbon {
	left: number;						// % depuis le 1er janvier de l'année affichée
	width: number;					// % de l'année
	clippedStart: boolean;	// commence AVANT le 1er janvier (arrêt à cheval sur deux années)
	clippedEnd: boolean;		// finit APRÈS le 31 décembre
}

// Largeur minimale d'un segment, en % d'année : un jour vaut 0,27 % — invisible. On garantit une
// marque lisible, quitte à mentir de deux jours sur une réglette qui en compte 365.
const MIN_RIBBON_WIDTH = 0.6;

// Jours écoulés depuis l'époque, en UTC : la seule arithmétique de dates qui ne dépende ni du
// fuseau du poste ni du changement d'heure (voir shared/time.ts, même principe).
function utcDay(dat: string): number | null {
	if (!dat || dat.length < 10) return null;
	const y = +dat.slice(0, 4), m = +dat.slice(5, 7), d = +dat.slice(8, 10);
	if (!y || !m || !d) return null;
	return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
}

export function daysInYear(year: number): number {
	return (utcDay(year + 1 + '-01-01') ?? 0) - (utcDay(year + '-01-01') ?? 0);
}

// Position d'un jour sur la réglette, en % — hors [0;100] s'il tombe hors de l'année.
export function yearPositionOf(dat: string, year: number): number | null {
	const day = utcDay(dat), origin = utcDay(year + '-01-01');
	if (day === null || origin === null) return null;
	return ((day - origin) / daysInYear(year)) * 100;
}

// Le segment d'une absence sur la réglette de l'année affichée.
//
// Une absence OUVERTE n'a pas de fin : on la trace jusqu'à AUJOURD'HUI, avec `clippedEnd` pour que
// l'écran la termine en dégradé plutôt que par un bord net. Dessiner un bord franc à la dernière
// date connue affirmerait une fin que personne n'a saisie.
export function leaveRibbonOf(
	a: { datestart: string; dateend: string; open?: boolean },
	year: number,
	today?: string,
): LeaveRibbon {
	const total = daysInYear(year);
	const origin = utcDay(year + '-01-01');
	const s = utcDay(a.datestart), e = utcDay(a.dateend);
	if (origin === null || s === null || e === null) {
		return { left: 0, width: 0, clippedStart: false, clippedEnd: false };
	}

	const todayDay = a.open ? utcDay(today ?? '') : null;
	const rawStart = s - origin;
	// +1 : les bornes sont INCLUSES, un jour seul doit occuper un jour de réglette.
	const rawEnd = Math.max(e, todayDay ?? e) - origin + 1;

	const start = Math.max(0, Math.min(rawStart, total));
	const end = Math.max(start, Math.min(rawEnd, total));
	const width = Math.max(((end - start) / total) * 100, MIN_RIBBON_WIDTH);
	const left = Math.min((start / total) * 100, 100 - width);
	return {
		left: round2(Math.max(0, left)),
		width: round2(width),
		clippedStart: rawStart < 0,
		// Une absence en cours n'a pas de fin CONNUE : son bord droit est toujours « à suivre »,
		// même quand elle tient dans l'année.
		clippedEnd: rawEnd > total || a.open === true,
	};
}

// Les douze repères de mois de la réglette : position exacte, pas un douzième chacun (février ne
// vaut pas juillet, et un segment de trois jours se lirait dans le mauvais mois).
export interface MonthTick { label: string; left: number; width: number; }

const MONTH_INITIALS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

export function monthTicksOf(year: number): MonthTick[] {
	const total = daysInYear(year);
	const origin = utcDay(year + '-01-01') ?? 0;
	return MONTH_INITIALS.map((label, i) => {
		const from = (utcDay(year + '-' + pad2(i + 1) + '-01') ?? origin) - origin;
		const to = i === 11 ? total : (utcDay(year + '-' + pad2(i + 2) + '-01') ?? origin) - origin;
		return { label, left: round2((from / total) * 100), width: round2(((to - from) / total) * 100) };
	});
}

// ============================================================
// Segments — ce qui se dessine sur une réglette
// ============================================================
//
// Une réglette porte UN segment par absence en mode détaillé, N en mode groupé. Le même type sert
// les deux, si bien que la géométrie des barres ne se dédouble pas entre les deux modes.
export interface RibbonSegment {
	id: number;
	ribbon: LeaveRibbon;
	color: string;
	state: number;
	open: boolean;
	lane: number;			// couche d'empilement (0 = la première), voir packRibbonLanes
}

export function leaveSegmentOf(
	r: { id: number; ribbon: LeaveRibbon; typColor: string; state: number; isOpen: boolean },
	lane = 0,
): RibbonSegment {
	return { id: r.id, ribbon: r.ribbon, color: r.typColor, state: r.state, open: r.isOpen, lane };
}

// Deux périodes qui se chevauchent ne peuvent pas partager la même bande : la plus courte
// DISPARAÎT sous la plus longue, et une absence invisible est pire qu'une absence mal placée. On
// les répartit en COUCHES — chaque barre prend la première couche libre à sa gauche.
//
// La tolérance vaut la largeur minimale d'un segment : un jour isolé est dessiné plus large qu'il
// ne dure (sinon il serait invisible), et sans cette tolérance il pousserait sur une deuxième
// couche l'absence du lendemain qu'il ne recouvre en réalité que de deux pixels.
//
// Au-delà de `maxLanes`, on empile sur la dernière plutôt que de laisser grandir la ligne sans
// fin : un salarié à six absences simultanées relève de la saisie, pas de l'affichage.
export function packRibbonLanes(bars: { left: number; width: number }[], maxLanes = 4): number[] {
	const order = bars.map((_, i) => i).sort((a, b) => bars[a].left - bars[b].left || a - b);
	const lanes = new Array<number>(bars.length).fill(0);
	const ends: number[] = [];			// fin (%) de la dernière barre posée sur chaque couche

	for (const i of order) {
		const bar = bars[i];
		let lane = ends.findIndex(end => end <= bar.left + MIN_RIBBON_WIDTH);
		if (lane < 0) lane = ends.length < maxLanes ? ends.length : maxLanes - 1;
		lanes[i] = lane;
		ends[lane] = Math.max(ends[lane] ?? 0, bar.left + bar.width);
	}
	return lanes;
}

// ============================================================
// Mode GROUPÉ — une personne, une ligne
// ============================================================
//
// « Une ligne par absence » répond à la question du responsable qui traite des demandes. Elle ne
// répond pas à « qui s'absente, et combien » : les douze congés d'un même salarié sont dispersés
// dans la liste, et son année ne se voit nulle part.
//
// Le mode groupé retourne le tableau — une personne, une ligne, toute son année sur la même
// réglette. Le texte des périodes n'y tient évidemment plus : il passe dans le panneau de survol,
// d'où `entries`, la même information en toutes lettres.

export interface LeaveGroupEntry {
	id: number;
	period: string;
	days: number;
	typ: string;
	typColor: string;
	state: number;
	stateLabel: string;
	isOpen: boolean;
}

export interface LeaveGroup {
	id_employee: number;
	employee: string;
	store: string;
	count: number;
	days: number;
	pending: number;				// demandes en attente — le travail à faire dans ce groupe
	lanes: number;					// hauteur de la réglette, en couches
	segments: RibbonSegment[];
	entries: LeaveGroupEntry[];
}

export interface LeaveGroupSource extends LeaveGroupEntry {
	id_employee: number;
	employee: string;
	store: string;
	datestart: string;
	ribbon: LeaveRibbon;
}

// Les groupes rangés comme la liste détaillée : ce qui attend une décision d'abord, puis par nom.
export function compareLeaveGroups(a: LeaveGroup, b: LeaveGroup): number {
	const pa = a.pending > 0 ? 0 : 1;
	const pb = b.pending > 0 ? 0 : 1;
	if (pa !== pb) return pa - pb;
	return a.employee.localeCompare(b.employee) || a.id_employee - b.id_employee;
}

export function leaveGroupsOf(rows: LeaveGroupSource[]): LeaveGroup[] {
	const byEmployee = new Map<number, LeaveGroupSource[]>();
	for (const r of rows) {
		const list = byEmployee.get(r.id_employee);
		if (list) list.push(r); else byEmployee.set(r.id_employee, [r]);
	}

	const groups: LeaveGroup[] = [];
	for (const [id_employee, list] of byEmployee) {
		// CHRONOLOGIQUE : le panneau de survol se lit dans l'ordre où les segments se posent sur la
		// réglette, de janvier à décembre. L'inverse obligerait à lire le texte à l'envers du dessin.
		const sorted = list.slice().sort((a, b) => a.datestart.localeCompare(b.datestart) || a.id - b.id);
		const lanes = packRibbonLanes(sorted.map(r => r.ribbon));
		groups.push({
			id_employee,
			employee: sorted[0].employee,
			store: sorted[0].store,
			count: sorted.length,
			days: sorted.reduce((total, r) => total + r.days, 0),
			pending: sorted.filter(r => r.state === HR_ABSENCE_STATE_REQUESTED).length,
			lanes: Math.max(1, ...lanes.map(l => l + 1)),
			segments: sorted.map((r, i) => leaveSegmentOf(r, lanes[i])),
			entries: sorted.map(r => ({
				id: r.id, period: r.period, days: r.days, typ: r.typ, typColor: r.typColor,
				state: r.state, stateLabel: r.stateLabel, isOpen: r.isOpen,
			})),
		});
	}
	return groups.sort(compareLeaveGroups);
}

// Le quadrillage des mois en UN dégradé CSS, calculé une fois par année et réutilisé par toutes
// les lignes : douze `<span>` par ligne feraient quelques milliers de nœuds sur un gros effectif,
// pour douze traits qui ne bougent jamais.
export function monthGridCss(year: number, color = '#e2e6ef'): string {
	const stops = monthTicksOf(year).slice(1).flatMap(t => [
		`transparent ${t.left}%`,
		`${color} ${t.left}%`,
		`${color} calc(${t.left}% + 1px)`,
		`transparent calc(${t.left}% + 1px)`,
	]);
	return 'linear-gradient(to right, ' + stops.join(', ') + ')';
}

function round2(n: number): number {
	return Math.round(n * 100) / 100;
}

function pad2(n: number): string {
	return (n < 10 ? '0' : '') + n;
}

// ============================================================
// Recherche libre
// ============================================================

// Signes diacritiques combinants (U+0300..U+036F) — écrits par échappement : le fichier est en
// UTF-8 et une classe de caractères composée de marques combinantes y serait illisible.
const COMBINING_MARKS = new RegExp('[\\u0300-\\u036f]', 'g');

// Sans accent ni casse : « sert » doit trouver « Sema Sert », « asry » doit trouver « Asry ».
export function normalizeSearch(s: string | null | undefined): string {
	return (s ?? '').normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase().trim();
}
